'use client'

import { useState, useEffect } from 'react'
import DealThumbnail from './DealThumbnail'
import { FeaturedTagType } from '@/collections/FeaturedTag/constants'
import List from '@/components/content/common/List'
import { FeaturedPlacement } from '@/lib/analytics'
import { createLogger } from '@/lib/logger'
import { queryPayload } from '@/lib/payloadClient'
import { determineDealsToDisplay } from '@/components/content/deals/helpers'
import type { Deal } from '@/payload-types'

const logger = createLogger('DealsTrending')

type DealsTrendingProps = {
  current?: number
}

export default function DealsTrending({ current }: DealsTrendingProps) {
  const [deals, setDeals] = useState<Deal[]>([])

  useEffect(() => {
    let isMounted = true
    const currentDate = new Date().toISOString()
    const hourBucket = currentDate.slice(0, 13)

    async function fetchAndSetTopDeals() {
      try {
        const { docs: dealDocs } = await queryPayload({
          collection: 'deal',
          select: {
            archived: false,
            createdAt: false,
            updatedAt: false,
            dealCustomers: false,
            product: false,
            _status: false,
          },
          populate: {
            app: {
              slug: true,
              name: true,
              partnershipTier: true,
              affiliateLink: true,
              deals: true,
              icon: true,
              glyph: true,
              brandBackgroundColor: true,
              brandPrimaryColor: true,
              brandSecondaryColor: true,
              brandTertiaryColor: true,
            },
            link: {
              clickValue: true,
              comments: true,
              dubId: true,
              name: true,
              passReferrerUrl: true,
              shortLink: true,
            },
            media: {
              url: true,
              alt: true,
              filename: true,
              coveragePercent: true,
              xOffsetPercent: true,
              yOffsetPercent: true,
            },
          },
          where: {
            and: [
              { archived: { in: [false, null] } },
              { app: { exists: true } },
              { featuredOrder: { greater_than: 0 } },
            ],
            or: [
              {
                // Both dates are null (always active)
                startDate: { equals: null },
                endDate: { equals: null },
              },
              {
                // Start date in past or null, end date in future
                startDate: { less_than_equal: currentDate },
                endDate: { greater_than_equal: currentDate },
              },
              {
                // Start date in past or null, no end date
                startDate: { less_than_equal: currentDate },
                endDate: { equals: null },
              },
              {
                // No start date, end date in future or null
                startDate: { equals: null },
                endDate: { greater_than_equal: currentDate },
              },
            ],
          },
          sort: ['featuredOrder', '-claimed'],
          pagination: false,
          depth: 2,
          limit: 8,
          cacheKey: `query:deal:trending:v4:${hourBucket}`,
        })

        const visibleDeals = determineDealsToDisplay(
          dealDocs.filter((deal) => deal.id !== current),
        ).slice(0, 3)
        setDeals(visibleDeals)
      } catch (error) {
        if (isMounted) {
          logger.error('Error fetching trending deals:', error)
        }
      }
    }

    fetchAndSetTopDeals()

    return () => {
      isMounted = false
    }
  }, [current])

  if (!deals || deals.length === 0) {
    return null
  }

  // Client-fetched on mount, so it pops in after the initial render — Meticulous snapshots
  // race the fetch and flag it as present-vs-absent. It sits at the bottom of the sidebar, so
  // ignoring its region (both ignore mechanisms, per docs/testing/meticulous.md) is safe.
  return (
    <div data-review="ignore" className="meticulous-ignore">
      <List
        items={deals}
        header={{
          title: 'Top deals right now',
          subtitle: 'Recommended',
          buttons: [{ label: 'See all', link: '/deals' }],
          tag: 'span',
        }}
        ListItem={({ item }: { item: Deal }) => (
          <DealThumbnail
            deal={item}
            type={FeaturedTagType.List}
            placement={FeaturedPlacement.SIDEBAR_DEALS}
          />
        )}
        className="hidden md:flex"
      />
    </div>
  )
}
